import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CategoriaService } from '../../core/services/categoria.service';
import { ImagenService } from '../../core/services/imagen.service';
import { AlertService } from '../../core/services/alert.service';
import { CategoriaApi } from '../../core/models/api/categoria-api';
import { PhotoGalleryEditorComponent, PhotoSlot } from '../../shared/components/photo-gallery-editor.component';
import { UiLoadingComponent } from '../../shared/components/ui-loading.component';
import { UiEmptyImageComponent } from '../../shared/components/ui-empty-image.component';

@Component({
    selector: 'app-admin-categorias',
    standalone: true,
    imports: [CommonModule, FormsModule, PhotoGalleryEditorComponent, UiLoadingComponent, UiEmptyImageComponent],
    template: `
    <div class="mb-6">
      <h2 class="text-2xl font-black text-gray-800">Categorías</h2>
      <p class="text-sm text-gray-400 mt-1">Arman el menú de la tienda y las fotos del carrusel.</p>
    </div>

    <app-ui-loading *ngIf="loading()"></app-ui-loading>

    <div *ngIf="!loading()" class="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <form class="lg:col-span-5 bg-white rounded-[2rem] p-6 border border-rose-100 shadow-sm flex flex-col gap-4 h-fit" (ngSubmit)="guardar()">
        <p class="text-sm font-black text-gray-800">{{ editUuid() ? 'Editar categoría' : 'Nueva categoría' }}</p>

        <app-photo-gallery-editor
          [photos]="fotos()"
          (photosChange)="fotos.set($event)"
          [multiple]="false"
          title="Foto"
          hint="Se muestra primero y se sube al guardar.">
        </app-photo-gallery-editor>

        <label class="block">
          <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Nombre</span>
          <input [ngModel]="nombre()" (ngModelChange)="nombre.set($event)" name="nombre" required placeholder="Rosas" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
        </label>
        <label class="block">
          <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Categoría padre</span>
          <select [ngModel]="padreUuid()" (ngModelChange)="padreUuid.set($event)" name="padreUuid" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200 bg-white">
            <option [ngValue]="null">Sin padre (menú principal)</option>
            <option *ngFor="let c of filas()" [ngValue]="c.uuid">{{ prefix(c) }}{{ c.nombre }}</option>
          </select>
        </label>
        <label class="block">
          <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Orden</span>
          <input [ngModel]="orden()" (ngModelChange)="orden.set($event)" name="orden" type="number" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
        </label>
        <label class="flex items-center gap-2 text-sm text-gray-700 font-medium">
          <input type="checkbox" [ngModel]="mostrarEnMenu()" (ngModelChange)="mostrarEnMenu.set($event)" name="mostrarEnMenu" class="accent-rose-500" />
          Mostrar en el menú
        </label>

        <div class="flex gap-2">
          <button class="flex-1 py-3 rounded-2xl text-white font-bold text-sm" style="background-color: var(--bg-main);">
            {{ editUuid() ? 'Actualizar' : 'Crear categoría' }}
          </button>
          <button *ngIf="editUuid()" type="button" (click)="cancelar()" class="px-4 py-3 rounded-2xl text-sm font-bold text-gray-500 border border-gray-200">
            Cancelar
          </button>
        </div>
      </form>

      <div class="lg:col-span-7 bg-white rounded-[2rem] border border-rose-100 overflow-hidden shadow-sm">
        <div *ngFor="let c of filas()" class="flex items-center gap-3 px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-rose-50/40">
          <div class="w-12 h-12 rounded-xl overflow-hidden bg-[#fff5f7] shrink-0" [style.marginLeft.px]="c.profundidad * 16">
            <img *ngIf="c.urlImagen" [src]="c.urlImagen" [alt]="c.nombre" class="w-full h-full object-cover" />
            <app-ui-empty-image *ngIf="!c.urlImagen" iconClass="w-5 h-5"></app-ui-empty-image>
          </div>
          <div class="min-w-0 flex-1">
            <p class="font-bold text-gray-800 truncate">{{ c.nombre }}</p>
            <p class="text-xs text-gray-400">{{ c.slug }} · {{ c.cantidadProductos }} productos</p>
          </div>
          <div class="flex gap-2 text-xs font-bold shrink-0">
            <button type="button" (click)="editar(c)" class="text-rose-500">Editar</button>
            <button *ngIf="confirmUuid() !== c.uuid" type="button" (click)="confirmUuid.set(c.uuid)" class="text-gray-400">Quitar</button>
            <button *ngIf="confirmUuid() === c.uuid" type="button" (click)="eliminar(c)" class="text-rose-600">¿Seguro?</button>
          </div>
        </div>
      </div>
    </div>
    `
})
export class AdminCategoriasComponent implements OnInit {
    private categoriaService = inject(CategoriaService);
    private imagenService = inject(ImagenService);
    private alerts = inject(AlertService);

    filas = signal<(CategoriaApi & { profundidad: number })[]>([]);
    nombre = signal('');
    padreUuid = signal<string | null>(null);
    orden = signal(0);
    mostrarEnMenu = signal(true);
    editUuid = signal<string | null>(null);
    imagenUuid = signal<string | null>(null);
    fotos = signal<PhotoSlot[]>([]);
    confirmUuid = signal<string | null>(null);
    loading = signal(true);

    ngOnInit(): void {
        this.reload();
    }

    prefix(c: { profundidad: number }): string {
        return c.profundidad > 0 ? `${'— '.repeat(c.profundidad)}` : '';
    }

    reload() {
        this.loading.set(true);
        this.categoriaService.invalidate();
        this.categoriaService.listCategorias().subscribe({
            next: (cats) => {
                const rows: (CategoriaApi & { profundidad: number })[] = [];
                const walk = (items: CategoriaApi[], profundidad: number) => {
                    for (const item of items ?? []) {
                        rows.push({ ...item, profundidad });
                        if (item.hijos?.length) {
                            walk(item.hijos, profundidad + 1);
                        }
                    }
                };
                walk(cats, 0);
                this.filas.set(rows);
                this.loading.set(false);
            },
            error: () => {
                this.loading.set(false);
                this.alerts.error('No se pudieron cargar las categorías.');
            }
        });
    }

    editar(c: CategoriaApi) {
        this.editUuid.set(c.uuid);
        this.nombre.set(c.nombre);
        this.padreUuid.set(c.padreUuid);
        this.orden.set(c.orden);
        this.mostrarEnMenu.set(c.mostrarEnMenu);
        this.imagenUuid.set(c.imagenUuid);
        this.fotos.set(c.urlImagen ? [{ previewUrl: c.urlImagen, imagenUuid: c.imagenUuid, file: null }] : []);
    }

    cancelar() {
        this.editUuid.set(null);
        this.nombre.set('');
        this.padreUuid.set(null);
        this.orden.set(0);
        this.mostrarEnMenu.set(true);
        this.imagenUuid.set(null);
        this.fotos.set([]);
    }

    guardar() {
        const pending = this.fotos().filter((f) => !!f.file).map((f) => f.file as File);
        if (pending.length) {
            this.imagenService.upload(pending).subscribe({
                next: (imgs) => this.enviar(imgs[0]?.uuid ?? this.imagenUuid()),
                error: (err) => this.alerts.error(err?.message || 'No se pudo subir la foto')
            });
            return;
        }
        this.enviar(this.fotos()[0]?.imagenUuid ?? null);
    }

    private enviar(imagenUuid: string | null) {
        const body = {
            padreUuid: this.padreUuid(),
            nombre: this.nombre(),
            mostrarEnMenu: this.mostrarEnMenu(),
            orden: Number(this.orden()) || 0,
            imagenUuid
        };
        const id = this.editUuid();
        const req$ = id
            ? this.categoriaService.updateCategoria(id, body)
            : this.categoriaService.createCategoria(body);

        req$.subscribe({
            next: (ok) => {
                if (ok) {
                    this.alerts.ok('Categoría guardada');
                    this.cancelar();
                    this.reload();
                } else {
                    this.alerts.error('No se pudo guardar');
                }
            }
        });
    }

    eliminar(c: CategoriaApi) {
        this.categoriaService.deleteCategoria(c.uuid).subscribe({
            next: (ok) => {
                this.confirmUuid.set(null);
                if (ok) {
                    this.alerts.ok('Categoría quitada');
                    this.reload();
                } else {
                    this.alerts.error('No se pudo quitar');
                }
            }
        });
    }
}
